import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { ModuleTemplate } from "../shared/common/ModuleTypes";


interface CreateEditModuleProps {
    currentModule: Partial<ModuleTemplate>;
    setCurrentModule: React.Dispatch<React.SetStateAction<Partial<ModuleTemplate>>>;
    onClose: () => void;
    onSave: () => void;
}

const CreateEditModule: React.FC<CreateEditModuleProps> = ({
    currentModule,
    setCurrentModule,
    onClose,
    onSave
}) => {
    const [isDefaultPromptOpen, setIsDefaultPromptOpen] = useState(false);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                <div className="p-6 border-b">
                    <h2 className="text-2xl font-bold text-gray-800">
                        {currentModule.id ? "Edit Module" : "Create Module"}
                    </h2>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                                type="text"
                                value={currentModule.title || ""}
                                onChange={e => setCurrentModule({ ...currentModule, title: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Module Title"
                            />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                                <input
                                    type="number"
                                    value={currentModule.order || 0}
                                    onChange={e =>
                                        setCurrentModule({
                                            ...currentModule,
                                            order: parseInt(e.target.value, 10)
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                                <select
                                    value={currentModule.level || "room"}
                                    onChange={e =>
                                        setCurrentModule({
                                            ...currentModule,
                                            level: e.target.value as "room" | "project"
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="room">Room</option>
                                    <option value="project">Project</option>
                                </select>
                            </div>
                            <div className="flex items-center pt-7">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={currentModule.optional || false}
                                        onChange={e =>
                                            setCurrentModule({
                                                ...currentModule,
                                                optional: e.target.checked
                                            })
                                        }
                                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Optional Module</span>
                                </label>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Main Question</label>
                            <textarea
                                value={currentModule.mainQuestion || ""}
                                onChange={e =>
                                    setCurrentModule({
                                        ...currentModule,
                                        mainQuestion: e.target.value
                                    })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                rows={3}
                                placeholder="What needs to be checked?"
                            />
                        </div>

                        <div>
                            <button
                                type="button"
                                onClick={() => setIsDefaultPromptOpen(prev => !prev)}
                                className="flex items-center gap-2 w-full text-left text-sm font-medium text-gray-700 mb-2 hover:text-gray-900"
                            >
                                <svg
                                    className={`w-4 h-4 transition-transform ${isDefaultPromptOpen ? "rotate-90" : ""}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                Default Prompt used
                            </button>
                            {isDefaultPromptOpen && (
                                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans overflow-x-auto">
{`If the user does not give the quantity of a material, ask a follow up about the quantity.

Note that appliances can be thought of as materials (for example: sinks, toilets, dishwasher, garbage disposal, range, etc..) and their quantity can be inferred as 1.
Also note that quantities do not have to be numeric - "all", "perimeter", "entire", etc.. are valid quantities of material.
You should not ask a follow up question about a quantity if the user says they're removing "all" of a material.

If the user does not give details on the following types of material, follow up by asking these questions:
- If they mention a "range" without the type (which is "gas" or "electric").  Ask "what type of range, gas or electric?"
- If they mention "cabinetry" or "cabinets" without the type (which is "full height" or "lower" or "upper").  Ask "what type of cabinetry, full height, lower or upper?"
- If they mention "countertop" without the type (which is "laminate", "granite" or "tile").  Ask "what type of countertop, laminate, granite or tile?"
- If they mention a "baseboard" or "base" without the size (which is "2 1/4" or "3 1/4" or "4 1/4").  Ask "what size of baseboard was removed?"
- If they mention "drywall" without the thickness (which is "1/2" or "5/8").  Ask "what was the thickness of the drywall removed?"

If the user does not give an answer about the type for the above materials AND doesn't give the quantity, ask for both.
Ask each follow up question one at a time.
For example: if a user says they "removed baseboard", you should respond "What quantity of baseboard was removed?" and then once they respond,
then ask "What was the size of the baseboard?"

Return an empty results array if there are no follow up questions to ask.`}
                                    </pre>
                                </div>
                            )}
                            <label className="block text-sm font-medium text-gray-700 mb-1">Follow Up Prompt</label>
                            <textarea
                                value={currentModule.followUpPrompt || ""}
                                onChange={e =>
                                    setCurrentModule({
                                        ...currentModule,
                                        followUpPrompt: e.target.value
                                    })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                rows={3}
                                placeholder="Optional custom prompt for this module"
                            />
                        </div>
                        <span className="text-xs text-gray-500">This will add to the existing follow up questions prompt, which already covers asking about missing quantities, please look at the existing prompt to structure your followup prompt</span>

                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700">Things to Cover</label>
                                <button
                                    onClick={() => {
                                        setCurrentModule({
                                            ...currentModule,
                                            suggestions: [
                                                ...(currentModule.suggestions || []),
                                                { id: uuidv4(), text: "", subText: "" }
                                            ]
                                        });
                                    }}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    + Add Suggestion
                                </button>
                            </div>
                            <div className="space-y-3">
                                {currentModule.suggestions?.map((suggestion, index) => (
                                    <div
                                        key={suggestion.id || index}
                                        className="flex gap-2 items-start bg-gray-50 p-3 rounded-md border border-gray-200"
                                    >
                                        <div className="flex-1 space-y-2">
                                            <input
                                                placeholder="Text (e.g., 'Drywall')"
                                                value={suggestion.text}
                                                onChange={e => {
                                                    const newSuggestions = [
                                                        ...(currentModule.suggestions || [])
                                                    ];
                                                    newSuggestions[index].text = e.target.value;
                                                    setCurrentModule({
                                                        ...currentModule,
                                                        suggestions: newSuggestions
                                                    });
                                                }}
                                                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm"
                                            />
                                            <input
                                                placeholder="Sub Text (e.g., 'Check for damage')"
                                                value={suggestion.subText}
                                                onChange={e => {
                                                    const newSuggestions = [
                                                        ...(currentModule.suggestions || [])
                                                    ];
                                                    newSuggestions[index].subText = e.target.value;
                                                    setCurrentModule({
                                                        ...currentModule,
                                                        suggestions: newSuggestions
                                                    });
                                                }}
                                                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm"
                                            />
                                        </div>
                                        <button
                                            onClick={() => {
                                                const newSuggestions = currentModule.suggestions?.filter(
                                                    (_, i) => i !== index
                                                );
                                                setCurrentModule({
                                                    ...currentModule,
                                                    suggestions: newSuggestions
                                                });
                                            }}
                                            className="text-red-500 hover:text-red-700 p-1"
                                            title="Remove item"
                                        >
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M6 18L18 6M6 6l12 12"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                                {(!currentModule.suggestions ||
                                    currentModule.suggestions.length === 0) && (
                                    <p className="text-sm text-gray-500 italic text-center py-2">
                                        No suggestions added yet
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-6 border-t bg-gray-50 flex justify-end gap-3 rounded-b-lg">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onSave}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Save Module
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateEditModule;

